/** Stable zone identity shared by charts, calendar previews and both builders.
 * The CSS roles keep each zone's hue while adapting ink to the current surface. */
export const TRAINING_ZONE_COLORS = [
  'var(--training-zone-1)', 'var(--training-zone-2)', 'var(--training-zone-3)',
  'var(--training-zone-4)', 'var(--training-zone-5)', 'var(--training-zone-6)',
  'var(--training-zone-7)', 'var(--training-zone-8)',
] as const;
export const DEFAULT_TRAINING_ZONE_NUMBERS = [1, 2, 3, 4, 5, 6, 7] as const;
export const UNCLASSIFIED_ZONE_COLOR = 'var(--training-zone-unclassified)';
export const TRAINING_ZONE_COLORS_BY_NUMBER: Readonly<Record<number, string>> =
  Object.fromEntries(TRAINING_ZONE_COLORS.map((color, index) => [index + 1, color]));
export const TRAINING_ZONE_GRADIENTS: [string, string][] = TRAINING_ZONE_COLORS.map(color => [color, color]);

export function trainingZoneColor(number: number): string {
  return Number.isInteger(number) ? TRAINING_ZONE_COLORS_BY_NUMBER[number] ?? UNCLASSIFIED_ZONE_COLOR : UNCLASSIFIED_ZONE_COLOR;
}

export function normalizeZoneDigits(label: string): string {
  return label.replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)));
}

/** A threshold range is a label, never a zone number (e.g. 'Zone 56–75%'). */
export function trainingZoneNumber(label: string): number | null {
  const match = normalizeZoneDigits(label).trim().match(/^(?:(?:z|zone|ناحیه|زون|منطقه|منطقة)\s*[-:]?\s*)?([1-9]\d*)$/i);
  return match ? Number(match[1]) : null;
}
/** Hide generated ordinal/percent labels when the row already shows its number and bounds. */
export function trainingZoneCustomName(label: string): string | undefined {
  const clean = label.replace(/[\u202a-\u202e\u2066-\u2069]/g, '').trim();
  const normalized = normalizeZoneDigits(clean);
  if (!clean || trainingZoneNumber(normalized) != null || /^(?:(?:zone|z|ناحیه|زون|منطقه|منطقة)\s*[:\-]?\s*)?\d+(?:[.٫]\d+)?(?:\s*[-–—:]\s*\d+(?:[.٫]\d+)?)?\s*[%٪]?$/i.test(normalized)) return undefined;
  return clean;
}
export function trainingZoneColorForLabel(label: string, fallbackIndex: number): string {
  return trainingZoneColor(trainingZoneNumber(label) ?? fallbackIndex + 1);
}

/** Presentation fallback for %FTP structures; never rewrites athlete zone settings. */
export function defaultPowerZoneForPercent(percent: number): number {
  if (percent <= 55) return 1;
  if (percent <= 75) return 2;
  if (percent <= 90) return 3;
  if (percent <= 105) return 4;
  if (percent <= 120) return 5;
  if (percent <= 150) return 6;
  return 7;
}
